<template>
	<div>
		<h2>General Rights</h2>

		<v-expansion-panels>
			<v-expansion-panel>
				<v-expansion-panel-header>
					<v-row no-gutters>
						<v-col cols="4">
							<strong>{{ 'Existing rights (' + rights.length + ')' }}</strong>
						</v-col>
					</v-row>
				</v-expansion-panel-header>
				<v-expansion-panel-content>
					<current-rights
						:rights="rights"
						:selected-user="selectedUser"
						@deleteRight="$emit('deleteRight', $event)"
						@confirmedChange="editExistingSpecialRights"
					/>
				</v-expansion-panel-content>
			</v-expansion-panel>
		</v-expansion-panels>

		<v-divider class="dividerNewRights"></v-divider>

		<v-row>
			<v-col>
				<v-autocomplete
					v-model="selectedApplication"
					:items="applicationList"
					:loading="loading"
					hide-no-data
					item-text="application_name"
					label="Report"
					placeholder="Grant rights to a report"
					prepend-icon="mdi-chart-box-plus-outline"
					clearable
					return-object
				/>
			</v-col>
			<v-col>
				<v-autocomplete
					v-model="selectedInstallation"
					:items="installationList"
					:loading="loading"
					hide-no-data
					item-text="mandator_description"
					label="Installation"
					placeholder="Installation for which report is granted"
					clearable
					return-object
				/>
			</v-col>
		</v-row>

		<v-switch
			v-model="switchSpecialRestrictions"
			:label="`Add special restrictions? (restrictions on serviceprovider, product, region, sales channel or reseller) ${switchSpecialRestrictions ? 'yes' : 'no'}`"
			:disabled="loading"
		/>

		<div v-show="switchSpecialRestrictions">
			<special-user-rights
				:installation="selectedInstallation || {}"
				:application="selectedApplication || {}"
				:selected-user="selectedUser"
				:specialRightsToggle="switchSpecialRestrictions"
				@serviceproviderChanged="(value) => selectedServiceprovider = value"
				@productChanged="(value) => selectedProduct = value"
				@regionChanged="(value) => selectedRegion = value"
				@salesChannelChanged="(value) => selectedSalesChannel = value"
				@resellerChanged="(value) => selectedReseller = value"
			/>
		</div>

		<v-btn
			class="mr-4 mb-4"
			color="primary"
			:disabled="!selectedApplication"
			@click="addRight"
		>
			Add Right
		</v-btn>
	</div>
</template>

<script>
/* global config */
import axios from 'axios'
import CurrentRights from './CurrentRights.vue'
import SpecialUserRights from './SpecialUserRights.vue'

export default {
	name: 'GeneralRightsEditor',
	components: {
		CurrentRights,
		SpecialUserRights,
	},
	props: {
		rights: { type: Array, required: true },
		selectedUser: { type: Object, required: true },
	},
	data() {
		return {
			loading: false,
			applicationList: [],
			selectedApplication: null,
			installationList: [],
			selectedInstallation: null,
			switchSpecialRestrictions: false,
			selectedServiceprovider: [],
			selectedProduct: [],
			selectedRegion: [],
			selectedSalesChannel: [],
			selectedReseller: [],
		}
	},
	methods: {
		async fetchFormData() {
			this.loading = true
			try {
				const [apps, installs] = await Promise.all([
					axios.get(`${config.baseAPIurl}/user-manager/application`),
					axios.get(`${config.baseAPIurl}/user-manager/installation`),
				])
				this.applicationList = apps.data
				this.installationList = installs.data
			} catch (error) {
				this.$emit('error', error)
			} finally {
				this.loading = false
			}
		},
		async addRight() {
			if (!this.selectedApplication) return
			try {
				await axios.post(`${config.baseAPIurl}/user-manager/rights`, {
					user_id: this.selectedUser.id,
					application_id: this.selectedApplication.id,
					application_shortname: this.selectedApplication.application_shortname,
					installation_id: this.selectedInstallation ? this.selectedInstallation.installation_id : null,
					product_ids: this.switchSpecialRestrictions && this.selectedProduct.length
						? this.selectedProduct.map((p) => ({ entity_id: p.entity_id, sku: p.sku })) : null,
					region_ids: this.switchSpecialRestrictions && this.selectedRegion.length
						? this.selectedRegion.map((r) => r.id) : null,
					reseller_ids: this.switchSpecialRestrictions && this.selectedReseller.length
						? this.selectedReseller.map((r) => r.id) : null,
					serviceprovider_ids: this.switchSpecialRestrictions && this.selectedServiceprovider.length
						? this.selectedServiceprovider.map((sp) => sp.id) : null,
					website_ids: this.switchSpecialRestrictions && this.selectedSalesChannel.length
						? this.selectedSalesChannel.map((sc) => sc.website_id) : null,
				})
				this.selectedApplication = null
				this.selectedInstallation = null
				this.switchSpecialRestrictions = false
				this.resetSpecialRights()
				this.$emit('rightAdded')
			} catch (error) {
				this.$emit('error', error)
			}
		},
		async editExistingSpecialRights(value) {
			const selectedUserRights = {
				rightId: value.rightBase[0].right_id,
				serviceprovider: value.editedSpecialRights.serviceprovider_id,
				region: value.editedSpecialRights.region_id,
				product: value.editedSpecialRights.product_sku,
				reseller: value.editedSpecialRights.reseller_id,
				salesChannel: value.editedSpecialRights.website_id,
			}
			this.$emit('confirmedEditRight', selectedUserRights)
		},
		resetSpecialRights() {
			this.selectedServiceprovider = []
			this.selectedRegion = []
			this.selectedProduct = []
			this.selectedReseller = []
			this.selectedSalesChannel = []
		},
	},
	watch: {
		selectedInstallation: {
			handler(value) {
				if (!value) this.resetSpecialRights()
			},
		},
		selectedApplication: {
			handler(value) {
				if (!value) this.resetSpecialRights()
			},
		},
	},
	created() {
		this.$nextTick(() => {
			this.fetchFormData()
		})
	},
}
</script>

<style lang="scss" scoped>
.dividerNewRights {
	margin-top: 25px;
	margin-bottom: 10px;
}
</style>
